import moment from "moment";
import { mwGet, mwPost, mapResponse, currentProfile } from "./client";
import { streamUrl } from "./playback";
import {
  readCache,
  createRequestQueue,
  getReadScope
} from "../../utils/readCache";
import { loadRequest } from "../../utils/loadRequest";

export const dateValue = (value) =>
  value
    ? moment(
        value,
        ["DD.MM.YYYY HH:mm:ss", "DD.MM.YYYY HH:mm", moment.ISO_8601],
        true
      ).valueOf()
    : null;
const interactions = (c) => [
  ...(c.IsInFavorites ? ["CHANNEL_FAV"] : []),
  ...(c.RequirePinCode ? ["CHANNEL_LOCK"] : [])
];
export const mapChannel = (c) => ({
  ...c,
  id: c.UId || c.ChannelUId,
  channelcode: c.UId || c.ChannelUId,
  name: c.Name,
  logo: c.PrimaryLogoImageUrl || c.PrimaryLogoUrl,
  no: c.RemoteNumber,
  displayOrder: c.DisplayOrder,
  genres: (c.Genres || []).map((g) => ({ id: g.UId, name: g.Name })),
  isAvailableToWatch: c.IsAvailableToWatch,
  isNpvrEnabled: c.IsNpvrEnabled,
  isTimeshiftEnable: c.IsDvrEnabled,
  interactions: interactions(c)
});
export const mapEpg = (e) => ({
  ...e,
  id: e.UId,
  epgProgramId: e.UId,
  prevuecode: e.UId,
  channelId: e.ChannelUId,
  channelUId: e.ChannelUId,
  title: e.Title,
  name: e.Title,
  description: e.ShortDescription,
  startTime: dateValue(e.StartDateTime),
  endTime: dateValue(e.EndDateTime),
  duration: e.Duration,
  vodUId: e.VodUId,
  hasCatchup: !!e.VodUId,
  isNpvrEnabled: e.IsNpvrEnabled,
  isRecordable: e.IsNpvrEnabled,
  hasNpvrRecord: e.HasNpvrRecord,
  requirePinCode: e.RequirePinCode,
  posters: [
    "Web Landscape",
    "Web Portrait",
    "HorizontalLow",
    "HorizontalHigh"
  ].map((type) => ({
    type,
    url:
      type === "Web Portrait"
        ? e.PortraitThumbnailImageUrl || e.ThumbnailImageUrl
        : e.LandscapeThumbnailImageUrl || e.ThumbnailImageUrl,
    posterType: type,
    posterUrl: e.ThumbnailImageUrl
  }))
});
export const channels = () =>
  readCache
    .read(
      "channels",
      async () => {
        const response = await loadRequest((options) =>
          mwGet("/channels", undefined, options)
        );
        if (!Array.isArray(response.data?.AllChannels))
          throw new Error("MW kanal listesi geçersiz.");
        return mapResponse(response, (data) =>
          data.AllChannels.map(mapChannel)
        );
      },
      30000
    )
    .then((r) => ({ ...r, data: JSON.parse(JSON.stringify(r.data)) }));
export const channelDetail = (id) =>
  mwGet("/channels/detail", { channelUId: id });
export const epgDetail = (id) => mwGet("/epg/detail", { EpgUId: id });
const queue = createRequestQueue(4);
export function programs(id, date) {
  const day = moment(
    date,
    ["DD.MM.YYYY", "YYYY-MM-DD", "YYYYMMDD", moment.ISO_8601],
    true
  );
  if (!day.isValid()) return Promise.reject(new Error("EPG tarihi geçersiz."));
  const scope = getReadScope();
  return readCache.read(
    `programs:${id}:${date}`,
    () =>
      queue(async () => {
        if (scope !== getReadScope()) throw new Error("Read context changed");
        const response = await loadRequest((options) =>
          mwGet(
            "/epg/list",
            {
              ChannelUId: id,
              Start: day.format("YYYY-MM-DD-00:00"),
              End: day.clone().add(1, "day").format("YYYY-MM-DD-00:00")
            },
            options
          )
        );
        if (!Array.isArray(response.data))
          throw new Error("MW program listesi geçersiz.");
        return mapResponse(response, (data) => data.map(mapEpg));
      }),
    120000
  );
}
export async function currentProgram(id) {
  const response = await programs(id, moment().format("YYYY-MM-DD"));
  return mapResponse(
    response,
    (items) =>
      items.find((p) => p.startTime <= Date.now() && p.endTime > Date.now()) ||
      null
  );
}
export async function channelPlay(id, startTime) {
  const response = await channelDetail(id);
  if (response.data.IsAvailableToWatch === false) {
    throw Object.assign(
      new Error("Kanal mevcut paketinizde izlemeye açık değil."),
      { code: "CONTENT_UNAVAILABLE" }
    );
  }
  // MW supplies its signed DVR URL; never alter its query/signature locally.
  return mapResponse(response, (data) =>
    streamUrl(data.StreamData, { dvr: !!startTime, startTime })
  );
}
export async function favorite(id) {
  const response = await mwPost("/favorites/toggle-channel", {
    ContentUId: id
  });
  const detail = await channelDetail(id);
  return mapResponse(response, (data) => [
    ...(data.added ? ["CHANNEL_FAV"] : []),
    ...(detail.data.RequirePinCode ? ["CHANNEL_LOCK"] : [])
  ]);
}
export async function lock(id, pin, profileId = currentProfile().id) {
  const response = await mwPost("/parental-controls/toggle-channel-lock", {
    ProfileUId: profileId,
    ChannelUId: id,
    PinCode: pin
  });
  // The channel control expects the full interaction list after a toggle.
  const detail = await channelDetail(id);
  return mapResponse(response, (data) => [
    ...(detail.data.IsInFavorites ? ["CHANNEL_FAV"] : []),
    ...(data.locked ? ["CHANNEL_LOCK"] : [])
  ]);
}
export const lockedChannels = async (profileId = currentProfile().id) =>
  mapResponse(
    await mwGet("/parental-controls/locked-channels", {
      ProfileUId: profileId
    }),
    (list) =>
      list.map((c) => ({ ...mapChannel(c), interactions: ["CHANNEL_LOCK"] }))
  );
export async function unlockAll(
  pin,
  columnCode,
  profileId = currentProfile().id
) {
  const response = await lockedChannels(profileId);
  for (const channel of response.data) await lock(channel.id, pin, profileId);
  const remaining = (await lockedChannels(profileId)).data;
  if (remaining.length)
    throw new Error("Kanal kilitlerinin tümü kaldırılamadı.");
  return {
    success: true,
    unlockedCount: response.data.length,
    failedChannels: [],
    remainingLockedChannels: []
  };
}
export async function genres() {
  return readCache.read(
    "channel-genres",
    async () => {
      const list = (await channels()).data,
        values = new Map();
      list.forEach((c) => c.genres.forEach((g) => values.set(g.id, g)));
      return [...values.values()];
    },
    300000
  );
}
